import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  inject,
  input,
  output,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { UntilDestroy } from '@ngneat/until-destroy';
import { ModusCheckboxModule, ModusIconModule } from '@trimble-gcs/modus';
import { FileSizePipe } from '@trimble-gcs/ngx-common';
import { TOGGLE_SELECTED } from '../../../../utils/toggle-selected';
import { ConnectTreeItem, ConnectTreeItemType } from '../connect-file-picker.models';
import { connectFileBrowserTableTranslations } from './connect-file-browser-table.translations';

@UntilDestroy()
@Component({
  selector: 'sd-connect-file-browser-table',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ModusCheckboxModule,
    ModusIconModule,
    MatTableModule,
    FileSizePipe,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './connect-file-browser-table.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConnectFileBrowserTableComponent {
  private readonly toggleSelected = inject(TOGGLE_SELECTED);
  readonly translations = connectFileBrowserTableTranslations();

  readonly treeItems = input<ConnectTreeItem[]>([]);
  readonly lastSelected = input<ConnectTreeItem | undefined>();
  readonly showPrevious = input(false);
  readonly disabled = input(false);

  readonly onPreviousClick = output();
  readonly onFolderClick = output<ConnectTreeItem>();
  readonly onSelectionChange = output<ConnectTreeItem[]>();

  private readonly data = computed(() => {
    const sortedTreeItems = this.treeItems().sort(
      (a, b) => b.type.localeCompare(a.type) || a.name.localeCompare(b.name),
    );
    return this.showPrevious() ? [this.backTreeItem, ...sortedTreeItems] : sortedTreeItems;
  });
  readonly dataSource = computed(() => new MatTableDataSource<ConnectTreeItem>(this.data()));

  private readonly isAllSelected = computed(() => {
    const treeItems = this.treeItems().filter((item) => item.type === ConnectTreeItemType.file);
    return treeItems.length > 0 && treeItems.every((item) => item.selected);
  });

  readonly selectAllControl = new FormControl(false, { nonNullable: true });
  readonly treeItemType = ConnectTreeItemType;
  readonly displayedColumns = [
    'selected',
    'thumbnailUrl',
    'name',
    'modifiedBy',
    'modifiedOn',
    'size',
  ];

  private readonly backTreeItem: ConnectTreeItem = {
    type: ConnectTreeItemType.previous,
    id: '',
    name: '...',
    size: 0,
    selected: false,
  };

  constructor() {
    this.createSelectAllEffect();
  }

  getItemId(index: number, item: ConnectTreeItem) {
    return item.id;
  }

  onItemClick(item: ConnectTreeItem, mouseEvent: MouseEvent) {
    if (this.disabled()) return;

    if (item.type === ConnectTreeItemType.previous) {
      this.onPreviousClick.emit();
    } else if (item.type === ConnectTreeItemType.folder) {
      this.onFolderClick.emit(item);
    } else if (item.type === ConnectTreeItemType.file) {
      const files = this.data().filter((item) => item.type === ConnectTreeItemType.file);
      const items = this.toggleSelected(files, item, mouseEvent, this.lastSelected());
      this.onSelectionChange.emit(items);
    }
  }

  toggleAllRows() {
    const selected = !this.isAllSelected();

    const items = this.treeItems()
      .filter((item) => item.type === ConnectTreeItemType.file)
      .map((item) => ({ ...item, selected }));

    this.onSelectionChange.emit(items);
  }

  checkboxKeyDown(treeItem: ConnectTreeItem) {
    treeItem.selected = !treeItem.selected;
    this.onSelectionChange.emit([treeItem]);
  }

  createSelectAllEffect() {
    effect(() => {
      this.selectAllControl.setValue(this.isAllSelected());
      if (this.disabled()) {
        this.selectAllControl.disable();
      } else {
        this.selectAllControl.enable();
      }
    });
  }
}
